import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios, { API } from '../utils/api';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, RefreshCw, Image, Sparkles, Trash2 } from 'lucide-react';
import notify from '../utils/notify';
import { confirmDialog } from '../components/ui/confirm-dialog';
import { callWithRetry } from '../utils/retry';
import ImageUploadPicker from '../components/shared/ImageUploadPicker';

const TEAL = '#4DB6AC';

export const EditPostPage = ({ TopHeader, BottomNav, useAuth }) => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [originalPost, setOriginalPost] = useState(null);
  const [formData, setFormData] = useState({ content: '', media_url: '', tags: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadedMediaUrl, setUploadedMediaUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  // Tracks whether an ImageUploadPicker upload is currently in flight.
  // Gates the save button and surfaces an inline message if the user
  // tries to save before the POST /api/upload response lands.
  const [uploading, setUploading] = useState(false);
  const [uploadStillInFlightError, setUploadStillInFlightError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [rephrasing, setRephrasing] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [aiImagePreview, setAiImagePreview] = useState(null);
  const [imageGenError, setImageGenError] = useState('');

  const btnStyle = {
    padding: '12px 24px', background: TEAL, color: 'white', border: 'none',
    borderRadius: 12, cursor: 'pointer', fontWeight: 600, fontSize: 14,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)', transition: 'all 0.2s ease',
    userSelect: 'none', WebkitUserSelect: 'none', WebkitTapHighlightColor: 'transparent'
  };
  const btnHover = (e) => { e.currentTarget.style.background = '#3aa690'; e.currentTarget.style.boxShadow = '0px 6px 12px rgba(0, 0, 0, 0.2)'; e.currentTarget.style.transform = 'translateY(-1px)'; };
  const btnLeave = (e) => { e.currentTarget.style.background = TEAL; e.currentTarget.style.boxShadow = '0px 4px 8px rgba(0, 0, 0, 0.15)'; e.currentTarget.style.transform = 'translateY(0)'; };
  const btnActive = (e) => { e.currentTarget.style.transform = 'translateY(2px)'; e.currentTarget.style.boxShadow = '0px 2px 4px rgba(0, 0, 0, 0.1)'; };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/posts`);
        const post = response.data.find(p => p.id === postId);
        if (!post) { notify.error('Post not found'); navigate('/profile'); return; }
        if (post.user_id !== user?.id) { notify.error('You can only edit your own posts'); navigate('/profile'); return; }
        setOriginalPost(post);
        setFormData({ content: post.content || '', media_url: post.media_url || '', tags: (post.tags || []).join(', ') });
        if (post.media_url) setPreviewUrl(post.media_url);
      } catch (error) { console.error('Failed to fetch post:', error); notify.error('Failed to load post'); navigate('/profile'); }
      finally { setLoading(false); }
    };
    if (user) fetchPost();
  }, [postId, user, navigate]);

  useEffect(() => {
    if (!originalPost) return;
    const originalTags = (originalPost.tags || []).join(', ');
    setHasChanges(
      formData.content !== (originalPost.content || '') ||
      selectedFile !== null ||
      uploadedMediaUrl !== '' ||
      formData.media_url !== (originalPost.media_url || '') ||
      formData.tags !== originalTags
    );
  }, [formData, selectedFile, uploadedMediaUrl, originalPost]);

  const handleRemoveMedia = () => {
    setSelectedFile(null); setPreviewUrl(''); setUploadedMediaUrl(''); setAiImagePreview(null);
    setFormData(prev => ({ ...prev, media_url: '' })); setUploadProgress(0);
  };

  const handleRephrase = async () => {
    if (!formData.content.trim()) return;
    setRephrasing(true);
    try {
      // Auto-retry once on failure — the AI provider flakes intermittently.
      const res = await callWithRetry(() => axios.post(`${API}/rephrase-text`, { text: formData.content }));
      setFormData(prev => ({ ...prev, content: res.data.rephrased_text }));
    } catch (err) {
      notify.error(err?.response?.data?.detail || 'Failed to rephrase. Please try again.');
    }
    finally { setRephrasing(false); }
  };

  const handleGenerateAIImage = async () => {
    setImageGenError('');
    if (!formData.content.trim()) { setImageGenError('Please write something first so we can generate a relevant image.'); return; }
    setGeneratingImage(true); setAiImagePreview(null);
    try {
      const res = await axios.post(`${API}/generate-image`, { post_text: formData.content });
      setAiImagePreview(`data:${res.data.mime_type || 'image/png'};base64,${res.data.image_data}`);
    } catch (err) { setImageGenError(err.response?.data?.detail || 'Failed to generate image. Please try again.'); }
    finally { setGeneratingImage(false); }
  };

  const handleConfirmAIImage = () => {
    if (!aiImagePreview) return;
    setPreviewUrl(aiImagePreview); setSelectedFile(null);
    fetch(aiImagePreview).then(r => r.blob()).then(blob => {
      setSelectedFile(new File([blob], 'ai-generated.png', { type: 'image/png' }));
    });
    setAiImagePreview(null); setImageGenError('');
  };

  const handleBack = () => { hasChanges ? setShowDiscardModal(true) : navigate('/profile'); };

  const handleDelete = async () => {
    const ok = await confirmDialog({
      title: 'Delete this post?',
      message: 'This will permanently remove the post from your profile and the feed.',
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/posts/${postId}`);
      notify.success('Post deleted');
      // Return to the Posts tab on the ME profile.
      navigate('/profile?tab=posts', { replace: true, state: { refresh: Date.now() } });
    } catch (error) {
      notify.error('Failed to delete post: ' + (error?.response?.data?.detail || error?.message || 'Unknown error'));
    } finally { setDeleting(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!hasChanges) return;
    // Block if a manual upload is still in flight — otherwise
    // uploadedMediaUrl would still be '' and the save would drop the
    // image the user just picked.
    if (uploading) {
      setUploadStillInFlightError('Your image is still uploading — please wait a moment and try again.');
      return;
    }
    setUploadStillInFlightError('');
    setSaving(true);
    try {
      // uploadedMediaUrl is populated by ImageUploadPicker for manual
      // uploads. selectedFile only holds AI-derived blobs — those still
      // upload at submit time, unchanged from before.
      let mediaUrl = uploadedMediaUrl || formData.media_url;
      if (!uploadedMediaUrl && selectedFile) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedFile);
        const uploadResponse = await axios.post(`${API}/upload`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (ev) => setUploadProgress(Math.round((ev.loaded * 100) / ev.total))
        });
        mediaUrl = uploadResponse.data.url;
      }
      const tags = formData.tags.split(',').map(tag => tag.trim()).filter(Boolean);
      await axios.patch(`${API}/posts/${postId}`, { content: formData.content, media_url: mediaUrl || null, tags });
      setShowSuccessToast(true);
      setSaved(true);
      setTimeout(() => navigate('/profile', { replace: true }), 1500);
    } catch (error) {
      notify.error('Failed to update post: ' + (error?.response?.data?.detail || error?.message || 'Unknown error'));
    } finally { setSaving(false); }
  };

  const previewTags = formData.tags.split(',').map(tag => tag.trim()).filter(Boolean);

  if (loading) {
    return (
      <div className="page-container">
        <TopHeader title="Edit Post" showBack={true} onBack={() => navigate('/profile')} />
        <div className="content-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', color: '#6B7280' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <AnimatePresence>
        {showSuccessToast && (
          <motion.div initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -50 }}
            style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', background: '#10B981',
              color: 'white', padding: '14px 24px', borderRadius: 12, fontSize: 14, fontWeight: 600, zIndex: 3000,
              boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>&#10003;</span> Post updated successfully
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDiscardModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}
            onClick={() => setShowDiscardModal(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              style={{ background: 'white', borderRadius: 16, padding: 24, maxWidth: 320, width: '100%', textAlign: 'center' }}
              onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1F2937', marginBottom: 8 }}>Discard Changes?</h3>
              <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 20 }}>You have unsaved changes. Are you sure you want to go back?</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => setShowDiscardModal(false)}
                  style={{ flex: 1, padding: 12, background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Keep Editing
                </button>
                <button onClick={() => navigate('/profile')}
                  style={{ flex: 1, padding: 12, background: '#EF4444', color: 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Discard
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="page-container">
        <div style={{ position: 'sticky', top: 0, background: 'white', zIndex: 100, borderBottom: '1px solid #E5E7EB',
          padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={handleBack} data-testid="edit-back-btn" style={{ background: 'none', border: 'none', padding: 8, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8, color: '#374151', fontSize: 14, fontWeight: 500 }}>
            <ChevronLeft size={20} />
          </button>
          <h1 style={{ fontSize: 16, fontWeight: 600, color: '#1F2937' }}>Edit Post</h1>
          <div style={{ width: 60 }} />
        </div>

        <div className="content-wrapper">
          <div className="content-section" style={{ padding: '16px' }}>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1F2937', marginBottom: 8, textAlign: 'left' }}>
                  What&#39;s on your mind?
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Share your thoughts..."
                  rows={5} required data-testid="edit-content"
                  style={{ width: '100%', boxSizing: 'border-box', padding: 14, borderRadius: 12, border: `2px solid #E5E7EB`,
                    fontSize: 15, resize: 'vertical', outline: 'none', transition: 'border-color 0.2s', fontFamily: 'inherit' }}
                  onFocus={(e) => e.target.style.borderColor = TEAL}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
                <button type="button" data-testid="edit-rephrase-btn" onClick={handleRephrase}
                  disabled={rephrasing || !formData.content.trim()}
                  style={{ ...btnStyle, marginTop: 8, padding: '8px 16px', fontSize: 13,
                    background: rephrasing ? '#9CA3AF' : TEAL,
                    opacity: !formData.content.trim() ? 0.5 : 1,
                    cursor: !formData.content.trim() ? 'not-allowed' : 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  onMouseEnter={!rephrasing && formData.content.trim() ? btnHover : undefined}
                  onMouseLeave={!rephrasing && formData.content.trim() ? btnLeave : undefined}
                  onMouseDown={!rephrasing && formData.content.trim() ? btnActive : undefined}
                  onMouseUp={!rephrasing && formData.content.trim() ? btnLeave : undefined}>
                  {rephrasing ? (<><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Rephrasing...</>)
                    : (<><Sparkles size={14} /> Rephrase with AI</>)}
                </button>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1F2937', marginBottom: 8, textAlign: 'left' }}>
                  Add Photo
                </label>

                {previewUrl && (
                  <div style={{ position: 'relative', marginBottom: 12, borderRadius: 12, overflow: 'hidden' }}>
                    <img src={previewUrl} alt="Post media" style={{ width: '100%', maxHeight: 300, borderRadius: 12, objectFit: 'cover' }} />
                    <button type="button" onClick={handleRemoveMedia} data-testid="remove-media-btn"
                      style={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <X size={16} />
                    </button>
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, background: 'rgba(255,255,255,0.3)' }}>
                        <div style={{ height: '100%', width: `${uploadProgress}%`, background: TEAL, transition: 'width 0.3s' }} />
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12 }}>
                  <ImageUploadPicker
                    testId="edit-choose-photo-btn"
                    disabled={generatingImage}
                    style={{ flex: 1 }}
                    onUploadStart={(dataUrl) => { setPreviewUrl(dataUrl); setAiImagePreview(null); setSelectedFile(null); setUploading(true); setUploadStillInFlightError(''); }}
                    onProgress={setUploadProgress}
                    onUploadComplete={(url) => { setUploadedMediaUrl(url); setPreviewUrl(url); setUploadProgress(0); setUploading(false); setUploadStillInFlightError(''); }}
                    onUploadError={() => { setPreviewUrl(''); setUploadedMediaUrl(''); setUploadProgress(0); setUploading(false); }}
                  />
                  <button type="button" data-testid="edit-generate-ai-btn" onClick={handleGenerateAIImage}
                    disabled={generatingImage}
                    style={{ ...btnStyle, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      background: generatingImage ? '#9CA3AF' : TEAL,
                      cursor: generatingImage ? 'not-allowed' : 'pointer' }}
                    onMouseEnter={!generatingImage ? btnHover : undefined}
                    onMouseLeave={!generatingImage ? btnLeave : undefined}
                    onMouseDown={!generatingImage ? btnActive : undefined}
                    onMouseUp={!generatingImage ? btnLeave : undefined}>
                    {generatingImage ? (<><RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating...</>)
                      : (<><Sparkles size={16} /> Generate</>)}
                  </button>
                </div>

                {imageGenError && (
                  <div data-testid="edit-image-gen-error" style={{
                    marginTop: 10, padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA',
                    borderRadius: 10, fontSize: 13, color: '#B91C1C', lineHeight: 1.4 }}>
                    {imageGenError}
                  </div>
                )}

                {generatingImage && (
                  <div style={{ marginTop: 16, textAlign: 'center', padding: 24, background: '#F9FAFB', borderRadius: 12 }}>
                    <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', color: TEAL }} />
                    <p style={{ marginTop: 10, fontSize: 13, color: '#6B7280' }}>Generating image from your post...</p>
                  </div>
                )}

                {aiImagePreview && !generatingImage && (
                  <div style={{ marginTop: 12 }}>
                    <img src={aiImagePreview} alt="AI Generated" style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 12 }} />
                    <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                      <button type="button" data-testid="edit-confirm-ai-image" onClick={handleConfirmAIImage}
                        style={{ ...btnStyle, flex: 1, padding: '10px 16px' }}
                        onMouseEnter={btnHover} onMouseLeave={btnLeave} onMouseDown={btnActive} onMouseUp={btnLeave}>
                        Use This Image
                      </button>
                      <button type="button" onClick={handleGenerateAIImage}
                        style={{ ...btnStyle, flex: 1, padding: '10px 16px', background: '#F3F4F6', color: '#374151',
                          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)' }}>
                        Regenerate
                      </button>
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8 }}>Supported: JPG, PNG, GIF, WEBP (Max 10MB)</div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#1F2937', marginBottom: 8, textAlign: 'left' }}>
                  Tags (comma-separated)
                </label>
                <input type="text" value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="wellness, walking, health" data-testid="edit-tags"
                  style={{ width: '100%', boxSizing: 'border-box', padding: 14, borderRadius: 12, border: `2px solid #E5E7EB`,
                    fontSize: 15, outline: 'none', transition: 'border-color 0.2s' }}
                  onFocus={(e) => e.target.style.borderColor = TEAL}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
                {previewTags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                    {previewTags.map((tag, idx) => (
                      <span key={idx} style={{ background: `${TEAL}18`, color: TEAL, padding: '4px 10px',
                        borderRadius: 6, fontSize: 13, fontWeight: 500 }}>
                        {tag.startsWith('#') ? tag : `#${tag}`}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {originalPost && (
                <div style={{ background: '#F9FAFB', borderRadius: 12, padding: 16, border: '1px solid #E5E7EB' }}>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>Post Details (cannot be edited)</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#374151' }}>
                    <span>Created: {new Date(originalPost.created_at).toLocaleDateString()}</span>
                    <span>{originalPost.likes?.length || 0} likes</span>
                  </div>
                </div>
              )}

              {uploadStillInFlightError && (
                <div
                  data-testid="edit-post-upload-error"
                  style={{ fontSize: 12, color: '#DC2626', marginBottom: 8, textAlign: 'center' }}
                >
                  {uploadStillInFlightError}
                </div>
              )}
              <button type="submit" disabled={saving || saved || !hasChanges || uploading} data-testid="save-post-btn"
                style={{ ...btnStyle, width: '100%', padding: '14px 24px', fontSize: 16, borderRadius: 12,
                  background: !hasChanges ? '#E5E7EB' : saving ? '#9CA3AF' : TEAL,
                  color: hasChanges ? 'white' : '#9CA3AF',
                  cursor: hasChanges && !saving ? 'pointer' : 'not-allowed' }}
                onMouseEnter={hasChanges && !saving ? btnHover : undefined}
                onMouseLeave={hasChanges && !saving ? btnLeave : undefined}
                onMouseDown={hasChanges && !saving ? btnActive : undefined}
                onMouseUp={hasChanges && !saving ? btnLeave : undefined}>
                {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes to Save'}
              </button>

              {/* Delete button — destructive, separated below the primary CTA */}
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting || saving}
                data-testid="delete-post-btn"
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  fontSize: 14,
                  fontWeight: 600,
                  borderRadius: 12,
                  background: 'transparent',
                  color: deleting ? '#9CA3AF' : '#DC2626',
                  border: `1px solid ${deleting ? '#E5E7EB' : '#FECACA'}`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  cursor: deleting || saving ? 'not-allowed' : 'pointer',
                  marginTop: -8,
                }}
              >
                <Trash2 size={16} />
                {deleting ? 'Deleting…' : 'Delete Post'}
              </button>
            </form>
          </div>
        </div>
        <BottomNav active="profile" />
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};
